import { buildSearchBox } from '@coveo/headless';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getHeadlessEngine } from '@/libs/coveo/getHeadlessEngine';
import { trackInteraction } from '@/utils/analytics';
import { capitalizeWords } from '@/utils/stringFormat';

// Default Header search param, ex: ?q=
const PARAM_KEY = 'q';
const defaultValue = {
    coveo: {
        controller: {
            searchBox: null,
        },
    },
    searchValue: '',
    suggestions: [],
    handleSetSearchValue: () => {},
    handleSelectSuggestion: () => {},
    submitForm: () => {},
};
const CareersHeaderContext = React.createContext(defaultValue);

export const CareersHeaderProvider = (props) => {
    const { children, coveo, searchResultsPage } = props;
    const [suggestions, setSuggestions] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const controllerSearchBox = useMemo(() => {
        const engine = getHeadlessEngine('header', coveo);
        const controllerSearchBox = buildSearchBox(engine, {
            options: { numberOfSuggestions: 10 },
        });
        return controllerSearchBox;
    }, [coveo]);

    const handleSetSearchValue = useCallback(
        (value) => {
            if (value?.length >= 2) {
                controllerSearchBox.updateText(value);
            } else {
                controllerSearchBox.clear();
            }
            setSearchValue(value);
        },
        [controllerSearchBox],
    );

    const handleSubmit = useCallback(
        (value) => {
            if (!value) {
                return;
            }
            // Analytics
            trackInteraction({
                eventName: 'search',
                componentName: 'Navigation',
                componentTitle: 'navigation',
                interactionType: 'search_results',
                selector: 'search',
                searchTerm: value,
            });

            let newUrl;
            // Check if the searchResultsPage contains a CSR (AEM content link)
            if (searchResultsPage?.csr || searchResultsPage?.href?.includes('/content')) {
                // Use URLSearchParams to append the query parameter
                const params = new URLSearchParams();
                params.set(PARAM_KEY, value);
                newUrl = `${searchResultsPage.href}?${params.toString()}`;
            } else {
                // Directly combine the href with the search value
                newUrl = `${searchResultsPage?.href}${value}`;
            }

            window.location.href = newUrl;
        },
        [searchResultsPage],
    );

    const handleSelectSuggestion = useCallback(
        (item) => {
            if (!item) {
                controllerSearchBox.clear();
                return;
            }
            // Analytics
            trackInteraction({
                eventName: 'search',
                componentName: 'Navigation',
                componentTitle: 'navigation',
                interactionType: 'predictive_click_text',
                actionLabel: item.text,
                selector: 'search',
                searchTerm: item.text,
            });
            setSearchValue(item.text);
            handleSubmit(item.text);
        },
        [controllerSearchBox, handleSubmit],
    );

    const submitForm = useCallback(
        (event) => {
            event.preventDefault();
            handleSubmit(searchValue);
        },
        [handleSubmit, searchValue],
    );

    useEffect(() => {
        const unsubscribe = controllerSearchBox.subscribe(() => {
            const newSuggestions = controllerSearchBox.state.suggestions.map((s, i) => {
                return {
                    id: i,
                    text: capitalizeWords(s.rawValue),
                };
            });

            setSuggestions(newSuggestions);
        });
        return () => {
            // Unsubscribe from the searchbox
            unsubscribe();
        };
    }, [controllerSearchBox]);

    // Header context state
    const value = useMemo(
        () => ({
            searchValue,
            suggestions,
            handleSetSearchValue,
            handleSelectSuggestion,
            submitForm,
        }),
        [searchValue, suggestions, handleSetSearchValue, handleSelectSuggestion, submitForm],
    );

    return <CareersHeaderContext.Provider value={value}>{children}</CareersHeaderContext.Provider>;
};

export const useCareersHeaderContext = () => {
    const context = React.useContext(CareersHeaderContext);

    if (!context) {
        throw new Error(`'useCareersHeaderContext' must be used within a CareersHeaderProvider`);
    }

    return context;
};
